/** Creates an organisation and its owner. This is the only path by which an
 *  organisation comes into existence — no database access is involved. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";

import { Endorsement, Lockup } from "@/components/Logo";
import { ApiError, api, setAccessToken } from "@/lib/api";
import { useSession } from "@/lib/session";

const ZONES = [
  "Asia/Amman", "Europe/London", "Europe/Berlin", "Asia/Dubai",
  "America/New_York", "America/Los_Angeles", "Asia/Singapore", "UTC",
];

export function SignUp() {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [form, setForm] = useState({
    organization_name: "",
    timezone: "Asia/Amman",
    owner_name: "",
    owner_email: "",
    password: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  function update(key: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    if (form.password.length < 12) {
      setFieldErrors({ password: "Use at least 12 characters." });
      return;
    }
    setBusy(true);
    try {
      const body = await api.post<{ access_token: string }>("/auth/sign-up", form);
      setAccessToken(body.access_token);
      await refresh();
      navigate("/admin/onboarding");
    } catch (caught) {
      if (caught instanceof ApiError) {
        const fields = (caught.details.fields as { field: string; message: string }[]) ?? [];
        if (fields.length) {
          setFieldErrors(Object.fromEntries(fields.map((f) => [f.field, f.message])));
        }
        setError(caught.message);
      } else {
        setError("The organisation could not be created. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <div className="auth__panel">
        <Lockup height={32} />
        <form className="auth__form" onSubmit={submit}>
          <div className="stack" style={{ gap: "var(--space-2)" }}>
            <h1 className="t-h1">Create your organisation</h1>
            <p className="t-body ink-secondary">
              You will be the owner. Rooms, people and tablets come next.
            </p>
          </div>

          {error && (
            <div className="notice notice--error" role="alert">
              <span className="notice__bar" aria-hidden="true" />
              <div className="stack" style={{ gap: 2 }}>
                <p className="notice__title">Could not create the organisation</p>
                <p className="notice__body">{error}</p>
              </div>
            </div>
          )}

          <div className="field">
            <label className="field__label" htmlFor="org">Organisation name</label>
            <input id="org" className="input" required value={form.organization_name}
              onChange={(event) => update("organization_name", event.target.value)} />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="tz">Main time zone</label>
            <select id="tz" className="select" value={form.timezone}
              onChange={(event) => update("timezone", event.target.value)}>
              {ZONES.map((zone) => <option key={zone} value={zone}>{zone}</option>)}
            </select>
            <p className="field__hint">Rooms can each override this later.</p>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="name">Your name</label>
            <input id="name" className="input" required value={form.owner_name}
              onChange={(event) => update("owner_name", event.target.value)} />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="mail">Work e-mail</label>
            <input id="mail" className="input" type="email" required
              autoComplete="username" value={form.owner_email}
              aria-invalid={Boolean(fieldErrors.owner_email)}
              onChange={(event) => update("owner_email", event.target.value)} />
            {fieldErrors.owner_email && <p className="field__error">{fieldErrors.owner_email}</p>}
            <p className="field__hint">
              This address's domain becomes the organisation's default sign-in domain.
            </p>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="pw">Password</label>
            <input id="pw" className="input" type="password" required
              autoComplete="new-password" value={form.password}
              aria-invalid={Boolean(fieldErrors.password)}
              onChange={(event) => update("password", event.target.value)} />
            {fieldErrors.password ? (
              <p className="field__error">{fieldErrors.password}</p>
            ) : (
              <p className="field__hint">At least 12 characters. You can add single sign-on later.</p>
            )}
          </div>

          <button className="btn btn--primary btn--block" type="submit" disabled={busy}>
            {busy ? "Creating…" : "Create organisation"}
          </button>

          <p className="t-caption ink-tertiary">
            Already have an account? <Link to="/sign-in">Sign in</Link>.
          </p>
        </form>
        <Endorsement />
      </div>

      <aside className="auth__aside" aria-hidden="true">
        <div className="stack" style={{ gap: "var(--space-4)" }}>
          <p className="t-eyebrow" style={{ color: "rgba(255,255,255,0.5)" }}>Getting started</p>
          <p className="t-display" style={{ maxWidth: "16ch" }}>
            Ten minutes to your first booked room.
          </p>
        </div>
        <ol className="stack" style={{ gap: "var(--space-3)" }}>
          {[
            "Create the organisation and choose how people sign in",
            "Add a location and your first rooms",
            "Connect Microsoft 365 or Google Workspace, if you use one",
            "Register the door tablets and assign them to rooms",
          ].map((line, index) => (
            <li key={line} className="row" style={{ gap: "var(--space-3)", alignItems: "flex-start" }}>
              <span className="avatar" style={{ background: "rgba(255,255,255,0.1)", color: "#fff" }}>
                {index + 1}
              </span>
              <span className="t-body" style={{ color: "rgba(255,255,255,0.85)" }}>{line}</span>
            </li>
          ))}
        </ol>
        <span />
      </aside>
    </div>
  );
}
